<template>
  <b-modal
    id="address-search-modal"
    v-model="show"
    :centered="true"
    size="lg"
    title="Find Address using Google Maps"
    :no-close-on-backdrop="true"
    @shown="initAddressAutocomplete"
    @hidden="destroyAddressAutocomplete"
  >
    <div v-if="addressSearchResult" class="mb-3 border rounded p-3 bg-light">
      <div class="d-flex flex-wrap mb-1">
        <div class="address-field mr-2" :class="{ 'text-info': isChanged('street1'), 'text-decoration-underline': isChanged('street1'), 'font-weight-bold': isChanged('street1') }">
          <div class="address-field-label">Street 1</div>
          <div>{{ addressSearchResult.street1 || '—' }}</div>
        </div>
        <div class="address-field mr-2" :class="{ 'text-info': isChanged('street2'), 'text-decoration-underline': isChanged('street2'), 'font-weight-bold': isChanged('street2') }">
          <div class="address-field-label">Street 2</div>
          <div>{{ addressSearchResult.street2 || '—' }}</div>
        </div>
        <div class="address-field" :class="{ 'text-info': isChanged('city'), 'text-decoration-underline': isChanged('city'), 'font-weight-bold': isChanged('city') }">
          <div class="address-field-label">City</div>
          <div>{{ addressSearchResult.city || '—' }}</div>
        </div>
      </div>
      <div class="d-flex flex-wrap">
        <div class="address-field mr-2" :class="{ 'text-info': isChanged('state'), 'text-decoration-underline': isChanged('state'), 'font-weight-bold': isChanged('state') }">
          <div class="address-field-label">State / Region</div>
          <div>{{ addressSearchResult.state || '—' }}</div>
        </div>
        <div class="address-field mr-2" :class="{ 'text-info': isChanged('postalCode'), 'text-decoration-underline': isChanged('postalCode'), 'font-weight-bold': isChanged('postalCode') }">
          <div class="address-field-label">Postal Code</div>
          <div>{{ addressSearchResult.postalCode || '—' }}</div>
        </div>
        <div class="address-field" :class="{ 'text-info': isChanged('country'), 'text-decoration-underline': isChanged('country'), 'font-weight-bold': isChanged('country') }">
          <div class="address-field-label">Country</div>
          <div>{{ addressSearchResult.country || '—' }}</div>
        </div>
      </div>
    </div>

    <b-form-group label="Search for an address">
      <div ref="autocompleteContainer" class="address-autocomplete-container"></div>
    </b-form-group>

    <template v-slot:modal-footer>
      <div class="w-100">
        <b-button
          variant="primary"
          class="ml-2 float-right"
          :disabled="!addressSearchResult"
          @click="confirmAddressSelection"
        >
          Use this Address
        </b-button>
        <b-button
          variant="secondary"
          class="ml-2 float-right"
          @click="show = false"
        >
          Cancel
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'AddressSearchModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    initialAddress: {
      type: Object,
      default: function() { return {}; }
    }
  },
  data() {
    return {
      addressSearchResult: null
    };
  },
  computed: {
    show: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); }
    },
    initialQuery() {
      var parts = [
        this.initialAddress.street1,
        this.initialAddress.city,
        this.initialAddress.state,
        this.initialAddress.postalCode,
        this.initialAddress.country
      ].filter(function(p) { return p; });
      return parts.join(', ');
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.addressSearchResult = {
          street1: this.initialAddress.street1 || '',
          street2: this.initialAddress.street2 || '',
          city: this.initialAddress.city || '',
          state: this.initialAddress.state || '',
          postalCode: this.initialAddress.postalCode || '',
          country: this.initialAddress.country || ''
        };
      } else {
        this.addressSearchResult = null;
      }
    }
  },
  methods: {
    initAddressAutocomplete() {
      var self = this;

      var createWidget = function() {
        window.google.maps.importLibrary('places').then(function(places) {
          var container = self.$refs.autocompleteContainer;
          if (!container) return;
          container.innerHTML = '';

          var placeAutocomplete = new places.PlaceAutocompleteElement({
            types: ['address']
          });

          container.appendChild(placeAutocomplete);
          self._autocompleteInstance = placeAutocomplete;

          placeAutocomplete.style.width = '100%';
          placeAutocomplete.style.colorScheme = 'light';

          placeAutocomplete.addEventListener('gmp-select', function(event) {
            var place = event.placePrediction.toPlace();
            self.onPlaceSelected(place);
          });

          if (self.initialQuery) {
            placeAutocomplete.value = self.initialQuery;

            // Also set on the inner input directly to trigger the dropdown
            setTimeout(function() {
              var shadowRoot = placeAutocomplete.shadowRoot;
              var innerInput = shadowRoot ? shadowRoot.querySelector('input') : null;
              if (innerInput) {
                innerInput.value = self.initialQuery;
                innerInput.focus();
                innerInput.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
              }

              // Auto-populate breakdown with first prediction
              places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: self.initialQuery })
                .then(function(result) {
                  var suggestions = result.suggestions;
                  if (suggestions && suggestions.length > 0 && suggestions[0].placePrediction) {
                    var place = suggestions[0].placePrediction.toPlace();
                    self.onPlaceSelected(place);
                  }
                })
                .catch(function() {});
            }, 100);
          }
        });
      };

      // Already fully loaded
      if (window.google && window.google.maps && typeof window.google.maps.importLibrary === 'function') {
        createWidget();
        return;
      }

      // Script is already loading — wait for our global ready flag
      if (window.__googleMapsLoading__) {
        var waitInterval = setInterval(function() {
          if (window.google && window.google.maps && typeof window.google.maps.importLibrary === 'function') {
            clearInterval(waitInterval);
            createWidget();
          }
        }, 100);
        return;
      }

      // First load — inject script with callback so importLibrary is guaranteed ready
      window.__googleMapsLoading__ = true;
      window.__googleMapsReady__ = function() {
        delete window.__googleMapsReady__;
        delete window.__googleMapsLoading__;
        createWidget();
      };

      var script = document.createElement('script');
      script.id = 'google-maps-places-script';
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + process.env.VUE_APP_GOOGLE_MAPS_API_KEY + '&loading=async&callback=__googleMapsReady__';
      document.head.appendChild(script);
    },
    onPlaceSelected(place) {
      var self = this;

      if (!place) return;

      place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] })
        .then(function() {
          var components = place.addressComponents || [];

          if (!components.length) return;

          var get = function(type, useShort) {
            var comp = components.find(function(c) { return c.types && c.types.indexOf(type) !== -1; });
            return comp ? (useShort ? comp.shortText : comp.longText) : '';
          };

          var streetNumber = get('street_number', false);
          var route = get('route', false);
          var subpremise = get('subpremise', false);

          self.addressSearchResult = {
            street1: [streetNumber, route].filter(function(p) { return p; }).join(' '),
            street2: subpremise || '',
            city: get('locality', false)
              || get('sublocality', false)
              || get('administrative_area_level_2', false),
            state: get('administrative_area_level_1', true),
            postalCode: get('postal_code', false),
            country: get('country', true)
          };
        })
        .catch(function() {
          self.$swal('Error', 'Could not retrieve address details. Please try again.', 'error');
        });
    },
    isChanged(field) {
      if (!this.addressSearchResult) return false;
      var original = (this.initialAddress[field] || '').trim().toLowerCase();
      var current = (this.addressSearchResult[field] || '').trim().toLowerCase();
      return original !== current;
    },
    confirmAddressSelection() {
      if (!this.addressSearchResult) return;
      this.$emit('confirmed', this.addressSearchResult);
      this.show = false;
    },
    destroyAddressAutocomplete() {
      if (this.$refs.autocompleteContainer) {
        this.$refs.autocompleteContainer.innerHTML = '';
      }
      this._autocompleteInstance = null;
      this.addressSearchResult = null;
    }
  }
};
</script>

<style scoped>
.address-autocomplete-container {
  width: 100%;
}

.address-autocomplete-container gmp-placeautocomplete {
  width: 100%;
}

.address-field {
  flex: 1;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 4px 10px;
  margin-bottom: 6px;
}

.text-decoration-underline {
  text-decoration: underline;
}

.address-field-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 2px;
}
</style>
